// src/components/onboarding/StepPhoto.js
import React, { useRef, useState } from 'react';
import { Camera, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const StepPhoto = ({ data, onChange, errors }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      onChange({ _photoError: t('onboarding.stepPhoto.errorSize') });
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width < 400 || img.height < 400) {
        onChange({ _photoError: t('onboarding.stepPhoto.errorMin') });
        URL.revokeObjectURL(img.src);
        return;
      }
      setPreview(img.src);
      onChange({ profilePictureFile: file, _photoError: null });
    };
    img.src = URL.createObjectURL(file);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-3">
          <Camera size={28} />
        </div>
        <h2 className="text-lg sm:text-xl font-bold dark:text-gray-100">
          {t('onboarding.stepPhoto.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          {t('onboarding.stepPhoto.subtitle')}
        </p>
      </div>

      <div className="flex flex-col items-center gap-4">
        <button
          type="button"
          data-testid="photo-upload-btn"
          onClick={() => fileRef.current?.click()}
          className="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden hover:border-rose-400 transition"
        >
          {preview ? (
            <img
              src={preview}
              alt={t('onboarding.stepPhoto.previewAlt')}
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <Camera size={36} className="text-gray-400" />
          )}
        </button>

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="px-4 py-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-xl text-sm font-semibold hover:bg-rose-100 dark:hover:bg-rose-900/50 transition"
        >
          {preview ? t('onboarding.stepPhoto.changePhoto') : t('onboarding.stepPhoto.addPhoto')}
        </button>

        <p className="text-xs text-gray-400 text-center">
          {t('onboarding.stepPhoto.requirements')}
        </p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
        data-testid="photo-file-input"
      />

      {(errors.profilePictureFile || data._photoError) && (
        <div
          data-testid="photo-error"
          className="flex items-center justify-center gap-1 text-red-500 text-sm"
        >
          <AlertCircle size={14} />
          <span>{errors.profilePictureFile || data._photoError}</span>
        </div>
      )}
    </div>
  );
};

export default StepPhoto;
